import type { QuickSearchView } from '@/components/QuickSearch/quickSearchRoutes'
import type { QuickSearchCategoryName, QuickSearchResponse } from '@/types/searchTypes'
import { VpLoader } from '@vtmn-play/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { QuerySummary } from '@/components/QuickSearch/Parts'
import { QuickSearchChips } from '@/components/QuickSearch/QuickSearchChips'
import { isQuickSearchView } from '@/components/QuickSearch/quickSearchRoutes'
import { AllCategoriesView } from '@/components/QuickSearch/views/AllCategoriesView'
import { SingleCategoryView } from '@/components/QuickSearch/views/SingleCategoryView'
import { isValidQuickSearchQuery } from '@/utils/quickSearch'
import { SearchRetryAlert } from './SearchRetryAlert'
import { useQuickSearchResultsPageActions } from './useQuickSearchResultsPageActions'
import { useQuickSearchResultsPageData } from './useQuickSearchResultsPageData'
import './quick-search-results-page.scss'

interface QuickSearchLoadingStateProps {
  query: string
  scope: string
  selectedFilter: QuickSearchView
  isAllView: boolean
  allCategoryResults?: QuickSearchResponse
  selectedCategory: QuickSearchCategoryName
  onFilterChange: (view: QuickSearchView) => void
}

function QuickSearchLoadingState({
  query,
  scope,
  selectedFilter,
  isAllView,
  allCategoryResults,
  selectedCategory,
  onFilterChange,
}: Readonly<QuickSearchLoadingStateProps>) {
  const { t } = useTranslation()

  if (!isAllView && allCategoryResults) {
    return (
      <div className="quick-search-results-page">
        <div className="quick-search-results-page__header">
          <QuerySummary
            query={query}
            totalResults={allCategoryResults.categories[selectedCategory].total}
          />
        </div>
        <div className="quick-search-results-page__filters">
          <QuickSearchChips
            selectedView={selectedFilter}
            onSelectView={onFilterChange}
            response={allCategoryResults}
          />
        </div>
        <output
          className="quick-search-results-page__category-loading"
          data-testid="quick-search-category-loading"
        >
          <VpLoader size="small" aria-hidden="true" data-testid="quick-search-category-loader" />
          <span>{t('quick-search.initial-loading', { query, scope })}</span>
        </output>
      </div>
    )
  }

  return (
    <div className="quick-search-results-page">
      <div
        className="quick-search-results-page__initial-loading"
        data-testid="quick-search-initial-loading"
      >
        <output className="quick-search-results-page__initial-loading-status">
          {t('quick-search.initial-loading', { query, scope })}
        </output>
        <VpLoader size="large" aria-hidden="true" data-testid="quick-search-initial-loader" />
      </div>
    </div>
  )
}

export function QuickSearchResultsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const { season = '' } = useParams<{ season: string }>()

  const query = searchParams.get('query') || ''
  const normalizedQuery = query.trim()
  const filterParam = searchParams.get('filter')
  const selectedFilter: QuickSearchView = isQuickSearchView(filterParam) ? filterParam : 'all'
  const hasInvalidFilter = filterParam !== null && !isQuickSearchView(filterParam)
  const loadingScope = selectedFilter === 'all'
    ? t('quick-search.all')
    : t(`quick-search.category-${selectedFilter}`)

  const {
    allCategoryResults,
    categoryData,
    categorySearch,
    hasResultsData,
    isAllView,
    isError,
    isLoading,
    isRefetchError,
    refetch,
    selectedCategory,
  } = useQuickSearchResultsPageData(query, season, selectedFilter)

  const { handleFilterChange, handleSeeAll, onResultSelect } = useQuickSearchResultsPageActions({
    query,
    season,
    selectedView: selectedFilter,
    selectedCategory,
    allCategoryResults,
    categoryData,
    navigate,
    setSearchParams,
  })

  const isWaitingForResults = isLoading || !hasResultsData
  const totalResults = isAllView && allCategoryResults
    ? allCategoryResults.total
    : categoryData?.total ?? 0

  useEffect(() => {
    if (!hasInvalidFilter)
      return

    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete('filter')
    setSearchParams(nextSearchParams, { replace: true })
  }, [hasInvalidFilter, searchParams, setSearchParams])

  if (!normalizedQuery) {
    return (
      <output className="quick-search-results-page" data-testid="quick-search-empty-query">
        {t('quick-search.enter-query')}
      </output>
    )
  }

  if (!isValidQuickSearchQuery(normalizedQuery)) {
    return (
      <output className="quick-search-results-page" data-testid="quick-search-invalid-query">
        {t('quick-search.query-length-error')}
      </output>
    )
  }

  if (isError) {
    return <SearchRetryAlert testId="quick-search-error" onRetry={() => void refetch()} />
  }

  if (isWaitingForResults) {
    return (
      <QuickSearchLoadingState
        query={normalizedQuery}
        scope={loadingScope}
        selectedFilter={selectedFilter}
        isAllView={isAllView}
        allCategoryResults={allCategoryResults}
        selectedCategory={selectedCategory}
        onFilterChange={handleFilterChange}
      />
    )
  }

  return (
    <div className="quick-search-results-page">
      <div className="quick-search-results-page__header">
        <QuerySummary query={query} totalResults={totalResults} />
      </div>

      <div className="quick-search-results-page__filters">
        <QuickSearchChips
          selectedView={selectedFilter}
          onSelectView={handleFilterChange}
          response={isAllView ? allCategoryResults : undefined}
          categoryTotals={isAllView || !categoryData ? undefined : { [selectedCategory]: categoryData.total }}
        />
      </div>

      {isAllView
        ? allCategoryResults && (
          <AllCategoriesView
            query={query}
            response={allCategoryResults}
            onResultSelect={onResultSelect}
            onSeeAll={handleSeeAll}
          />
        )
        : categoryData && (
          <>
            {isRefetchError && (
              <SearchRetryAlert testId="quick-search-category-refetch-error" onRetry={() => void refetch()} />
            )}
            <SingleCategoryView
              query={query}
              categoryData={categoryData}
              onResultSelect={onResultSelect}
            />
            {categorySearch.isFetchingNextPage && (
              <output className="quick-search-results-page__loading" data-testid="quick-search-next-page-loader">
                <VpLoader size="small" />
                <span className="sr-only">{t('quick-search.loading')}</span>
              </output>
            )}
            {categorySearch.isFetchNextPageError && (
              <SearchRetryAlert testId="quick-search-next-page-error" onRetry={() => void categorySearch.fetchNextPage()} />
            )}
            <div ref={categorySearch.sentinelRef} data-testid="quick-search-results-sentinel" />
          </>
        )}
    </div>
  )
}
